import { City, Country } from 'country-state-city';
import { countryCodeToFlagEmoji } from './phone.js';

export type CountryOption = { code: string; label: string };
export type CityOption = { value: string; label: string };

export type AddressLocationInput = {
  line1?: string;
  country?: string | null;
  city?: string | null;
  postalCode?: string | null;
};

export type AddressLocationField = 'line1' | 'country' | 'city' | 'postalCode';

export type AddressLocationValidation = {
  ok: boolean;
  errors: Partial<Record<AddressLocationField, string>>;
};

const DEFAULT_COUNTRY = 'MA';
const LOCALE = 'fr';

let countriesCache: CountryOption[] | null = null;

function normalizeCountryCode(code: string | null | undefined): string {
  return (code?.trim().toUpperCase() || DEFAULT_COUNTRY).slice(0, 2);
}

function isKnownCountryCode(code: string): boolean {
  return Country.getAllCountries().some((c) => c.isoCode === code);
}

/** ISO-2 when `raw` is a known country code. Empty stays null. Invalid stays null (caller rejects). */
export function parseIsoCountry(raw: string | null | undefined): string | null {
  const t = raw?.trim().toUpperCase() ?? '';
  if (!t) return null;
  if (!/^[A-Z]{2}$/.test(t) || !isKnownCountryCode(t)) return null;
  return t;
}

/** ISO countries for selects (French labels when available). */
export function listCountries(): CountryOption[] {
  if (countriesCache) return countriesCache;
  let display: Intl.DisplayNames | undefined;
  try {
    display = new Intl.DisplayNames([LOCALE], { type: 'region' });
  } catch {
    display = undefined;
  }
  countriesCache = Country.getAllCountries()
    .map((c) => {
      const code = c.isoCode;
      const name = display?.of(code) ?? c.name;
      return {
        code,
        label: `${countryCodeToFlagEmoji(code)} ${name}`,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, LOCALE));
  return countriesCache;
}

/** Cities for a country (unique, sorted). Empty when dataset has none — caller may allow free text. */
export function listCitiesForCountry(countryCode: string | null | undefined): CityOption[] {
  const cc = normalizeCountryCode(countryCode);
  if (!isKnownCountryCode(cc)) return [];
  const list = City.getCitiesOfCountry(cc) ?? [];
  const seen = new Set<string>();
  const unique: CityOption[] = [];
  for (const c of list) {
    const n = c.name.trim();
    if (!n || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase());
    unique.push({ value: n, label: n });
  }
  unique.sort((a, b) => a.label.localeCompare(b.label, LOCALE));
  return unique;
}

function cityNamesMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function isCityValidForCountry(countryCode: string, city: string): boolean {
  const trimmed = city.trim();
  if (!trimmed) return false;
  const options = listCitiesForCountry(countryCode);
  if (options.length === 0) {
    return trimmed.length >= 2 && trimmed.length <= 80;
  }
  return options.some((o) => cityNamesMatch(o.value, trimmed));
}

function postalRequired(countryCode: string): boolean {
  return countryCode === 'MA';
}

function validatePostalCode(countryCode: string, postalCode: string | null | undefined): string | null {
  const raw = postalCode?.trim() ?? '';
  if (!raw) {
    return postalRequired(countryCode) ? 'Code postal requis.' : null;
  }
  if (countryCode === 'MA' || countryCode === 'FR') {
    return /^\d{5}$/.test(raw) ? null : 'Code postal invalide (5 chiffres).';
  }
  if (countryCode === 'US') {
    return /^\d{5}(-\d{4})?$/.test(raw) ? null : 'Code postal invalide.';
  }
  if (!/^[\dA-Za-z][\dA-Za-z\s-]{2,11}[\dA-Za-z]$/.test(raw)) {
    return 'Code postal invalide.';
  }
  return null;
}

/** Validates address location fields (French error messages). */
export function validateAddressLocation(
  input: AddressLocationInput,
  mode: 'create' | 'patch' = 'create',
): AddressLocationValidation {
  const errors: Partial<Record<AddressLocationField, string>> = {};

  if (mode === 'create' || input.line1 !== undefined) {
    const line1 = input.line1?.trim() ?? '';
    if (line1.length < 3) errors.line1 = 'Adresse trop courte (3 caractères minimum).';
    else if (line1.length > 200) errors.line1 = 'Adresse trop longue.';
  }

  const country =
    input.country !== undefined ? normalizeCountryCode(input.country) : undefined;
  if (mode === 'create' || input.country !== undefined) {
    const cc = country ?? DEFAULT_COUNTRY;
    if (!isKnownCountryCode(cc)) errors.country = 'Pays invalide.';
  }

  const effectiveCountry = country ?? DEFAULT_COUNTRY;

  if (mode === 'create' || input.city !== undefined) {
    const city = input.city?.trim() ?? '';
    if (!city) errors.city = 'Ville requise.';
    else if (!isCityValidForCountry(effectiveCountry, city)) {
      errors.city = 'Ville invalide pour ce pays.';
    }
  }

  if (mode === 'create' || input.postalCode !== undefined) {
    const postalErr = validatePostalCode(effectiveCountry, input.postalCode);
    if (postalErr) errors.postalCode = postalErr;
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

export { normalizeCountryCode, DEFAULT_COUNTRY as ADDRESS_DEFAULT_COUNTRY };
